<script>
    import {get, writable} from 'svelte/store';
    import ContentNavigationBar from 'views/components/contentNavigationBar/ContentNavigationBar.svelte'
    import ContentNavigationBarItem from 'views/components/contentNavigationBar/ContentNavigationBarItem.svelte'
    import Icon from 'views/components/icon/Icon.svelte'
    import Util from 'util/util'

    // Activity model
    export let model

    let currentTaskGroup
    let hasScrollToFlag = false

    // Make exported property as a Svelte store so non-Svelte views can interact with it.
    export const activeItemKey = writable(getInitialActiveItem())
    const activeItemKeyHistory = []

    activeItemKey.subscribe((value) => {
        currentTaskGroup = model.task_groups.findWhere({sequence: value})
        if (value === -1) {
            Backbone.history.navigate(`/activities/show/${model.id}`, {trigger: false})
        } else if (value === model.task_groups.size()) {
            Backbone.history.navigate(`/activities/show/${model.id}/end`, {trigger: false})
        } else if (currentTaskGroup) {
            // If the original URL had some kind of scroll-to parameter such as scrolling to a specific
            // piece of student feedback, replace the history to remove annoying redirects when the user
            // navigates to the previous page using the back button.
            Backbone.history.navigate(
                `/activities/show/${model.id}/${currentTaskGroup.id}`,
                {trigger: false, replace: hasScrollToFlag}
            )
            hasScrollToFlag = false
        }
        if (activeItemKeyHistory[activeItemKeyHistory.length - 1] !== value) {
            activeItemKeyHistory.push(value)
        }
    })

    export function getActiveItem() {
        return get(activeItemKey)
    }

    export function setActiveItem(newActiveItemKey) {
        activeItemKey.set(Math.min(model.task_groups.size(), Math.max(-1, newActiveItemKey)))
    }

    export function toPreviousItem() {
        setActiveItem(get(activeItemKey) - 1)
    }

    export function toNextItem() {
        setActiveItem(get(activeItemKey) + 1)
    }

    export function revertActiveItem() {
        // Remove current active item.
        activeItemKeyHistory.pop()
        // Remove last active item and try to navigate to it.
        const previousKey = activeItemKeyHistory.pop()
        if (previousKey === undefined) {
            toPreviousItem()
        } else {
            setActiveItem(previousKey)
        }
    }

    function getInitialActiveItem() {
        const initialActiveParams = window.app.controller.activeParams[1]
        if (initialActiveParams === 'end') {
            return model.task_groups.size()
        }
        if (model.task_groups.has(initialActiveParams)) {
            return model.task_groups.get(initialActiveParams).get('sequence')
        }
        // If second parameter refers to a specific task, get parent task group and prepare
        // to scroll to this task (see src/views/pages/activities/show/Show.js).
        if (/^task_id:\d+/.test(initialActiveParams)) {
            const gotoTask = model.tasks.get(initialActiveParams.substring(8))
            if (gotoTask) {
                gotoTask.set('showStudentAnswers', true)
                hasScrollToFlag = true
                return model.task_groups.get(gotoTask.get('task_group_id'))?.get('sequence')
            }
        }
        // If no valid URL parameter, set key to -1, which is the activity start page.
        return -1
    }

    function onNavigationKeyup(e) {
        if (ISMOBILE || Util.shouldPreventDocumentLevelKeyEvent(e)) {
            return
        }

        if (e.key === 'ArrowLeft') {
            toPreviousItem()
        } else if (e.key === 'ArrowRight') {
            toNextItem()
        } else if (
            Backbone.Model.user.getSetting('IsAvailableForContentTeam') &&
            e.key === 'w' &&
            currentTaskGroup
        ) {
            Backbone.history.navigate(
                `/task_groups/author/${model.id}/${currentTaskGroup.id}`,
                {trigger: true}
            )
        }
    }
</script>

<svelte:document on:keyup={onNavigationKeyup} />

{#if !window.ISMOBILE}
    <div class="navigation-bar-outer no-print">
        <ContentNavigationBar>
            <ContentNavigationBarItem
                itemKey={-1}
                {activeItemKey}
            >
                <Icon name="assignment"></Icon>
            </ContentNavigationBarItem>
            {#each model.task_groups.models as taskGroup (taskGroup.id)}
                <ContentNavigationBarItem
                    itemKey={taskGroup.get('sequence')}
                    {activeItemKey}
                    model={taskGroup}
                >
                    {@const contentDomain = taskGroup.getContentDomainItem()?.abbreviation}
                    {@const customIndexIcon = taskGroup.getCustomIndexIcon()}

                    <div class="index">
                        {#if customIndexIcon}
                            <Icon name={customIndexIcon}></Icon>
                        {:else}
                            {taskGroup.get('index')}
                        {/if}

                        {#if contentDomain}
                            <div class="content-domain">{contentDomain}</div>
                        {/if}
                    </div>
                </ContentNavigationBarItem>
            {/each}
            <ContentNavigationBarItem
                itemKey={model.task_groups.size()}
                {activeItemKey}
            >
                <Icon name="checkmark-circle"></Icon>
            </ContentNavigationBarItem>
        </ContentNavigationBar>
    </div>
{/if}

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.navigation-bar-outer {
  grid-area: menu-right;
  position: sticky;
  top: 0;
  height: 100vh;
  /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
  height: 100dvh;
  z-index: 5000;
  -webkit-overflow-scrolling: touch;
}

:global(body.has-fixed-sidebar) .navigation-bar-outer {
  /* Hide navigation bar when fixed sidebar is active to save on visual clutter */
  display: none;
}

.index {
  display: flex;
  justify-content: center;
  padding-block: 4px;
  width: 100%;
}

.content-domain {
  font-size: 12px;
  margin-top: 2px;
  margin-bottom: -4px;
  padding-top: 2px;
  padding-bottom: 2px;
  border-bottom-left-radius: 11px;
  border-bottom-right-radius: 11px;
  background-color: rgba(44.88, 123.42, 179.52, 0.1);
  color: hsl(205, 60%, 44%);
  text-transform: uppercase;
}

:global(button.is-active) .content-domain {
  color: #fff;
}</style>
